import * as React from "react";
import { filterPeopleOptions } from "../../redesign/table/filterModel";
import type { PeopleFilterOption } from "../../redesign/table/RdColumnFilter";
import { FEED_AUSTAUSCH_FILTER, type FeedAustauschFilter } from "../../../services/vorgangService";
import { AUSTAUSCH_FILTER_LABEL } from "../../../utils/vorgangFeedFilters";

/**
 * Der Filter „Art & Betreff" der Vorgangsliste — Betreff-Text UND Austausch
 * (PO 01.11.2026): „Austausch offen / mit mir / kein Austausch" und mit wem.
 *
 * Bis dahin hatte der Austausch eine eigene Spalte mit eigenem Filter. Die
 * Spalte gibt es weiter (werksseitig ausgeblendet), der Filter sitzt jetzt
 * hier, wo auch das Kennzeichen steht — unter dem Betreff. Eine zweite
 * Bedienung daneben gibt es bewusst nicht.
 *
 * Alles ist ein Entwurf, bis „Filtern" (oder Enter im Textfeld) ihn übernimmt
 * — so wirkt der Filter mit EINEM Neuladen, nicht mit einem je Klick.
 * Kollegen kommen als userIds, ein Externer als Namensteil; beide wirken nur
 * auf OFFENE Austausche, neben „Kein Austausch" sind sie deshalb gesperrt.
 */
export interface BetreffAustauschWerte {
    titel: string;
    austausch?: FeedAustauschFilter;
    mit: string[];
    extern: string;
}

export const istBetreffAustauschAktiv = (w: BetreffAustauschWerte): boolean =>
    w.titel.trim() !== "" || !!w.austausch || w.mit.length > 0 || w.extern.trim() !== "";

export const BetreffAustauschFilter: React.FC<{
    werte: BetreffAustauschWerte;
    personen: PeopleFilterOption[];
    onUebernehmen: (werte: BetreffAustauschWerte) => void;
    close: () => void;
}> = ({ werte, personen, onUebernehmen, close }) => {
    const [titel, setTitel] = React.useState(werte.titel);
    const [austausch, setAustausch] = React.useState<FeedAustauschFilter | undefined>(werte.austausch);
    const [mit, setMit] = React.useState<string[]>(werte.mit);
    const [extern, setExtern] = React.useState(werte.extern);
    const [suche, setSuche] = React.useState("");

    const ohne = austausch === "ohne";
    // Gewählte zuerst — sonst verschwände eine Wahl beim Weitersuchen aus dem Blick.
    const liste = React.useMemo(() => {
        const treffer = filterPeopleOptions(personen, suche);
        return [...treffer.filter((p) => mit.includes(p.id)), ...treffer.filter((p) => !mit.includes(p.id))];
    }, [personen, suche, mit]);

    const uebernehmen = () => {
        onUebernehmen({
            titel: titel.trim(),
            austausch,
            mit: ohne ? [] : mit,
            extern: ohne ? "" : extern.trim(),
        });
        close();
    };
    const zuruecksetzen = () => {
        onUebernehmen({ titel: "", austausch: undefined, mit: [], extern: "" });
        close();
    };
    const enter = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") uebernehmen();
    };

    return (
        <>
            <input
                className="rd-pop-input"
                autoFocus
                aria-label="Art & Betreff filtern"
                placeholder="Art & Betreff filtern …"
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                onKeyDown={enter}
            />

            <div className="rd-pop-grouptitle">Austausch</div>
            <div className="rd-pop-rollen" role="group" aria-label="Austausch">
                {FEED_AUSTAUSCH_FILTER.map((wert) => {
                    const an = austausch === wert;
                    return (
                        <button
                            key={wert}
                            type="button"
                            aria-pressed={an}
                            className={"rd-pop-toggle" + (an ? " on" : "")}
                            onClick={() => setAustausch(an ? undefined : wert)}
                        >
                            {AUSTAUSCH_FILTER_LABEL[wert]}
                        </button>
                    );
                })}
            </div>

            <div className="rd-pop-grouptitle">Austausch mit</div>
            {ohne ? (
                <div className="rd-pop-empty">Bei „Kein Austausch“ gibt es niemanden zu wählen.</div>
            ) : (
                <>
                    <input
                        className="rd-pop-input"
                        aria-label="Kollegen suchen"
                        placeholder="Kollegen suchen …"
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                    />
                    <div className="rd-pop-scroll" style={{ maxHeight: 168 }}>
                        {liste.map((o) => (
                            <label key={o.id} className="rd-pop-check">
                                <input
                                    type="checkbox"
                                    checked={mit.includes(o.id)}
                                    onChange={() =>
                                        setMit((m) => (m.includes(o.id) ? m.filter((x) => x !== o.id) : [...m, o.id]))
                                    }
                                />
                                <span className="rd-pop-avatar" aria-hidden>
                                    {o.photoUrl ? (
                                        <img src={o.photoUrl} alt="" />
                                    ) : (
                                        <span>{o.initials ?? o.label.slice(0, 2).toUpperCase()}</span>
                                    )}
                                </span>
                                <span className="rd-pop-avatar-label">{o.label}</span>
                            </label>
                        ))}
                        {liste.length === 0 && <div className="rd-pop-empty">Keine Person gefunden</div>}
                    </div>
                    <input
                        className="rd-pop-input"
                        aria-label="Externe Person (Name)"
                        placeholder="Externe Person, z. B. Kraus"
                        value={extern}
                        onChange={(e) => setExtern(e.target.value)}
                        onKeyDown={enter}
                    />
                </>
            )}

            <div className="rd-pop-foot">
                <button type="button" className="rd-pop-btn" onClick={zuruecksetzen}>
                    Reset
                </button>
                <button type="button" className="rd-pop-btn primary" onClick={uebernehmen}>
                    Filtern
                </button>
            </div>
        </>
    );
};
